/**
 * /facturas/nueva — Composer de factura con cálculo IVA + INC en vivo.
 *
 * Flujo:
 *   1. Selecciona cliente del catálogo (search dropdown)
 *   2. Agrega líneas:
 *      - Selecciona producto del catálogo → pre-llena defaults
 *      - O escribe línea inline (sin ProductID)
 *      - Edita cantidad, precio, IVA%, INC%, descuento
 *      - Cálculo subtotal + IVA + INC + total se actualiza en vivo
 *   3. Totales (subtotal + IVA total + INC total + grand total) en footer
 *   4. POST /admin/tax-clients/:id/manual-invoices (status='draft')
 *   5. Redirect a /facturas/:id (detail) tras éxito
 *
 * Cálculo en TS replica computeLine de invoicing_service.go (Number con
 * 2 decimales). Backend recalcula con shopspring/decimal (autoritativo).
 * UI solo muestra preview.
 */

import { useState, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2, Plus, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import {
  customerApi,
  invoiceApi,
  productApi,
  type CreateInvoiceLineRequest,
  type CreateInvoiceRequest,
  type Customer,
  type InvoiceDocType,
  type Product,
} from '@/lib/api'
import { ROUTES } from '@/lib/constants'
import { formatMoney, moneyFormatter } from '@/lib/money'

// Discrepancy codes DIAN (Anexo Técnico v1.9) — varían por doc_type.
const DISCREPANCY_CODES: Record<InvoiceDocType, { code: string; label: string }[]> = {
  invoice: [],
  credit_note: [
    { code: '1', label: '1 — Devolución parcial de los bienes' },
    { code: '2', label: '2 — Anulación de factura electrónica' },
    { code: '3', label: '3 — Rebaja total aplicada' },
    { code: '4', label: '4 — Descuento total aplicado' },
    { code: '5', label: '5 — Rescisión: nulidad por falta de requisitos' },
    { code: '6', label: '6 — Otros' },
  ],
  debit_note: [
    { code: '1', label: '1 — Intereses' },
    { code: '2', label: '2 — Gastos por cobrar' },
    { code: '3', label: '3 — Cambio del valor' },
    { code: '4', label: '4 — Otros' },
  ],
}

interface LineDraft {
  product_id?: string
  description: string
  quantity: string
  unit_price: string
  line_discount: string
  tax_iva_percent: string
  tax_iva_excluded: boolean
  tax_inc_percent: string
}

const EMPTY_LINE: LineDraft = {
  description: '',
  quantity: '1',
  unit_price: '',
  line_discount: '0',
  tax_iva_percent: '19.00',
  tax_iva_excluded: false,
  tax_inc_percent: '0.00',
}

// Replica computeLine de invoicing_service.go.
function computeLine(l: LineDraft) {
  const qty = parseFloat(l.quantity) || 0
  const unit = parseFloat(l.unit_price) || 0
  const discount = parseFloat(l.line_discount) || 0
  const ivaPct = parseFloat(l.tax_iva_percent) || 0
  const incPct = parseFloat(l.tax_inc_percent) || 0

  const gross = qty * unit
  const subtotal = round2(gross - discount)
  const iva = l.tax_iva_excluded ? 0 : round2((subtotal * ivaPct) / 100)
  const inc = round2((subtotal * incPct) / 100)
  const total = round2(subtotal + iva + inc)

  return { subtotal, iva, inc, total, valid: qty > 0 && unit > 0 && subtotal >= 0 }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

export function InvoiceCreatePage() {
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const nav = useNavigate()
  const [searchParams] = useSearchParams()

  // Deep-link: /facturas/nueva?doc_type=credit_note&ref=<invoiceId> permite
  // crear una nota directamente desde el detalle de una factura aceptada.
  const presetDocType = searchParams.get('doc_type')
  const [docType, setDocType] = useState<InvoiceDocType>(
    presetDocType === 'credit_note' || presetDocType === 'debit_note'
      ? presetDocType
      : 'invoice',
  )
  const [customerId, setCustomerId] = useState('')
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10))
  const [paymentMeans, setPaymentMeans] = useState('Contado')
  // Moneda + tasa de cambio (AT v1.9 FAR — R cuando moneda ≠ COP).
  // Defaults: COP sin tasa (caso 95% nacional). Si el usuario cambia
  // a USD/EUR debe ingresar la TRM (CalculationRate).
  const [currency, setCurrency] = useState('COP')
  const [exchangeRate, setExchangeRate] = useState('')
  // Anticipo pre-factura (AT v1.9 FBD). Default sin anticipo. Si el
  // usuario lo activa, debe llenar id + monto + fecha.
  const [hasPrepayment, setHasPrepayment] = useState(false)
  const [prepaidId, setPrepaidId] = useState('')
  const [prepaidAmount, setPrepaidAmount] = useState('')
  const [prepaidReceivedDate, setPrepaidReceivedDate] = useState('')
  const [prepaidPaidDate, setPrepaidPaidDate] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<LineDraft[]>([{ ...EMPTY_LINE }])
  const [customerSearch, setCustomerSearch] = useState('')

  // NC/ND specific. `ref` puede venir pre-seleccionada por deep-link.
  const [referencedInvoiceId, setReferencedInvoiceId] = useState(
    () => searchParams.get('ref') ?? '',
  )
  const [discrepancyReason, setDiscrepancyReason] = useState('')
  const [discrepancyCode, setDiscrepancyCode] = useState('1')

  const isNote = docType !== 'invoice'

  // Preview de montos en la moneda seleccionada para el documento (AT
  // v1.9 FAR) — no COP fijo.
  const money = moneyFormatter(currency)

  // Catálogo de clientes (búsqueda).
  const customers = useQuery({
    queryKey: ['inv-customers', activeOrgId, customerSearch],
    queryFn: () => customerApi.list(activeOrgId!, { search: customerSearch || undefined, limit: 50 }),
    enabled: !!activeOrgId,
  })

  // Catálogo de productos (todos, para dropdown per línea).
  const products = useQuery({
    queryKey: ['inv-products', activeOrgId],
    queryFn: () => productApi.list(activeOrgId!, { limit: 500 }),
    enabled: !!activeOrgId,
  })

  // Para NC/ND: facturas aceptadas DIAN (referenciables).
  const acceptedInvoices = useQuery({
    queryKey: ['accepted-invoices', activeOrgId],
    queryFn: () =>
      invoiceApi.list(activeOrgId!, { doc_type: 'invoice', status: 'accepted', limit: 200 }),
    enabled: !!activeOrgId && isNote,
  })

  const totals = useMemo(() => {
    let subtotal = 0
    let iva = 0
    let inc = 0
    let total = 0
    lines.forEach((l) => {
      const c = computeLine(l)
      subtotal += c.subtotal
      iva += c.iva
      inc += c.inc
      total += c.total
    })
    return {
      subtotal: round2(subtotal),
      iva: round2(iva),
      inc: round2(inc),
      total: round2(total),
    }
  }, [lines])

  const allLinesValid = lines.every((l) => computeLine(l).valid && l.description.trim() !== '')
  // Si moneda ≠ COP el tasa de cambio es R (AT FAR).
  const exchangeRateOK = currency === 'COP' || parseFloat(exchangeRate) > 0
  // Si hasPrepayment está activo, los 3 (id, monto, fecha recibido) son R.
  const prepaymentOK =
    !hasPrepayment ||
    (prepaidId.trim().length > 0 &&
      parseFloat(prepaidAmount) > 0 &&
      prepaidReceivedDate.length > 0)
  const canSubmit =
    !!customerId &&
    lines.length > 0 &&
    allLinesValid &&
    exchangeRateOK &&
    prepaymentOK &&
    (!isNote || (referencedInvoiceId && discrepancyReason.trim() !== ''))

  const create = useMutation({
    mutationFn: () => {
      const body: CreateInvoiceRequest = {
        doc_type: docType,
        customer_id: customerId,
        issue_date: issueDate,
        payment_means: paymentMeans,
        currency,
        ...(currency !== 'COP' && exchangeRate
          ? { exchange_rate: exchangeRate }
          : {}),
        ...(hasPrepayment && prepaidId && prepaidAmount && prepaidReceivedDate
          ? {
              prepaid_id: prepaidId,
              prepaid_amount: prepaidAmount,
              prepaid_received_date: prepaidReceivedDate,
              ...(prepaidPaidDate ? { prepaid_paid_date: prepaidPaidDate } : {}),
            }
          : {}),
        notes: notes || undefined,
        lines: lines.map<CreateInvoiceLineRequest>((l) => ({
          product_id: l.product_id || undefined,
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          line_discount: l.line_discount,
          tax_iva_percent: l.tax_iva_percent,
          tax_iva_excluded: l.tax_iva_excluded,
          tax_inc_percent: l.tax_inc_percent,
        })),
        ...(isNote && {
          referenced_invoice_id: referencedInvoiceId,
          discrepancy_reason: discrepancyReason,
          discrepancy_code: discrepancyCode,
        }),
      }
      return invoiceApi.create(activeOrgId!, body)
    },
    onSuccess: (inv) => {
      toast.success(`Factura ${inv.doc_number} creada (draft)`)
      nav(ROUTES.INVOICE_DETAIL.replace(':id', inv.id))
    },
    onError: (err: unknown) => {
      const e = err as {
        response?: { data?: { message?: string; error?: string } }
        message?: string
      }
      const msg =
        e?.response?.data?.message ?? e?.response?.data?.error ?? e?.message ?? 'unknown'
      // Para NC/ND el rechazo más común es que la factura referenciada no
      // sea una factura aceptada por DIAN — traducimos ese caso a un
      // mensaje accionable en vez del error crudo del backend.
      if (isNote && /referenc|accepted|acepta/i.test(msg)) {
        toast.error(
          `No se pudo crear la nota: ${msg}. La nota debe referenciar una factura (no otra nota) ya aceptada por DIAN.`,
        )
      } else {
        toast.error(`Error: ${msg}`)
      }
    },
  })

  const updateLine = (idx: number, patch: Partial<LineDraft>) => {
    setLines((arr) => arr.map((l, i) => (i === idx ? { ...l, ...patch } : l)))
  }

  const removeLine = (idx: number) => {
    setLines((arr) => arr.filter((_, i) => i !== idx))
  }

  const addLine = () => {
    setLines((arr) => [...arr, { ...EMPTY_LINE }])
  }

  const setProductAtLine = (idx: number, p: Product | null) => {
    if (!p) {
      updateLine(idx, { product_id: undefined })
      return
    }
    updateLine(idx, {
      product_id: p.id,
      description: p.name,
      unit_price: p.unit_price,
      tax_iva_percent: p.tax_iva_percent,
      tax_iva_excluded: p.tax_iva_excluded,
      tax_inc_percent: p.tax_inc_percent,
    })
  }

  if (authLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
        </div>
      </AppShell>
    )
  }

  const selectedCustomer = customers.data?.items.find((c: Customer) => c.id === customerId)

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl">
        <Link
          to={ROUTES.INVOICES}
          className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </Link>
        <h1 className="mt-2 text-3xl font-bold">
          {docType === 'invoice'
            ? 'Nueva factura'
            : docType === 'credit_note'
            ? 'Nueva nota crédito'
            : 'Nueva nota débito'}
        </h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Crea un draft. El cálculo IVA + INC se actualiza en vivo. Backend
          recalcula con precisión decimal al persistir (autoritativo).
        </p>

        {/* ─── Tipo de documento ──────────────────────────────── */}
        <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
            Tipo de documento
          </h2>
          <div className="mt-3 flex gap-2">
            {(['invoice', 'credit_note', 'debit_note'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setDocType(t)}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                  docType === t
                    ? 'border-brand-600 bg-brand-50 text-brand-700'
                    : 'border-border bg-bg-app text-fg-secondary hover:bg-bg-elevated'
                }`}
              >
                {t === 'invoice'
                  ? 'Factura'
                  : t === 'credit_note'
                  ? 'Nota crédito (NC)'
                  : 'Nota débito (ND)'}
              </button>
            ))}
          </div>
          {isNote && (
            <p className="mt-2 text-xs text-fg-muted">
              Las NC/ND requieren referencia a una factura previa <strong>aceptada por DIAN</strong>.
            </p>
          )}
        </section>

        {/* ─── NC/ND: referenced invoice + discrepancy ──────── */}
        {isNote && (
          <section className="mt-6 rounded-xl border border-yellow-300 bg-yellow-50 p-6 dark:bg-yellow-950 dark:border-yellow-800">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-yellow-900 dark:text-yellow-100">
              Documento referenciado *
            </h2>
            <div className="mt-3">
              <label className="block text-sm font-medium" htmlFor="ref_invoice">
                Factura a corregir/ajustar *
              </label>
              <select
                id="ref_invoice"
                value={referencedInvoiceId}
                onChange={(e) => setReferencedInvoiceId(e.target.value)}
                className={inputCls + ' mt-1'}
              >
                <option value="">Selecciona factura aceptada DIAN...</option>
                {acceptedInvoices.data?.items.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.doc_number} — {inv.customer_legal_name} ({formatMoney(inv.grand_total, inv.currency)})
                  </option>
                ))}
              </select>
              {acceptedInvoices.data && acceptedInvoices.data.items.length === 0 && (
                <p className="mt-1 text-xs text-error-fg">
                  Sin facturas aceptadas DIAN aún. NC/ND requieren factura previa aceptada.
                </p>
              )}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium" htmlFor="disc_code">
                  Código de discrepancia *
                </label>
                <select
                  id="disc_code"
                  value={discrepancyCode}
                  onChange={(e) => setDiscrepancyCode(e.target.value)}
                  className={inputCls + ' mt-1'}
                >
                  {DISCREPANCY_CODES[docType].map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium" htmlFor="disc_reason">
                  Razón * (texto libre, va al XML UBL)
                </label>
                <input
                  id="disc_reason"
                  type="text"
                  value={discrepancyReason}
                  onChange={(e) => setDiscrepancyReason(e.target.value)}
                  className={inputCls + ' mt-1'}
                  placeholder="ej: devolución del producto X por defecto de fábrica"
                />
              </div>
            </div>
          </section>
        )}

        {/* ─── Cliente + cabecera ─────────────────────────────── */}
        <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
            Adquirente y cabecera
          </h2>

          <div className="mt-4">
            <label className="block text-sm font-medium" htmlFor="customer_search">
              Cliente *
            </label>
            <input
              id="customer_search"
              type="text"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Buscar por razón social o NIT..."
              className={inputCls + ' mt-1'}
            />
            {customers.isLoading && (
              <p className="mt-1 text-xs text-fg-muted">Cargando...</p>
            )}
            {customers.data && customers.data.items.length > 0 && !selectedCustomer && (
              <ul className="mt-2 max-h-48 overflow-auto rounded-md border border-border bg-bg-app">
                {customers.data.items.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setCustomerId(c.id)
                        setCustomerSearch('')
                      }}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-bg-elevated"
                    >
                      <span className="font-medium">{c.legal_name}</span>
                      <span className="font-mono text-xs text-fg-muted">
                        {c.identification_type}-{c.identification_number}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {selectedCustomer && (
              <div className="mt-2 flex items-center justify-between rounded-md border border-brand-500/40 bg-brand-50/50 px-3 py-2 text-sm">
                <span className="font-medium">
                  {selectedCustomer.legal_name}{' '}
                  <span className="ml-2 font-mono text-xs text-fg-muted">
                    {selectedCustomer.identification_type}-{selectedCustomer.identification_number}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setCustomerId('')}
                  className="text-xs text-fg-muted hover:text-fg-primary"
                >
                  Cambiar
                </button>
              </div>
            )}
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium" htmlFor="issue_date">
                Fecha emisión *
              </label>
              <input
                id="issue_date"
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                className={inputCls + ' mt-1'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium" htmlFor="payment_means">
                Forma de pago
              </label>
              <select
                id="payment_means"
                value={paymentMeans}
                onChange={(e) => setPaymentMeans(e.target.value)}
                className={inputCls + ' mt-1'}
              >
                <option value="Contado">Contado</option>
                <option value="Crédito">Crédito</option>
              </select>
            </div>
          </div>

          {/* ─── Moneda + tasa de cambio (AT v1.9 FAR) ─────────── */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium" htmlFor="currency">
                Moneda *
              </label>
              <select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className={inputCls + ' mt-1'}
              >
                <option value="COP">COP — Peso colombiano</option>
                <option value="USD">USD — Dólar estadounidense</option>
                <option value="EUR">EUR — Euro</option>
                <option value="MXN">MXN — Peso mexicano</option>
                <option value="BRL">BRL — Real brasileño</option>
                <option value="CLP">CLP — Peso chileno</option>
                <option value="PEN">PEN — Sol peruano</option>
              </select>
            </div>
            {currency !== 'COP' && (
              <div>
                <label className="block text-sm font-medium" htmlFor="exchange_rate">
                  Tasa de cambio a COP (TRM) *
                </label>
                <input
                  id="exchange_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  placeholder="Ej: 4250.50"
                  className={inputCls + ' mt-1'}
                />
                <p className="mt-1 text-xs text-fg-muted">
                  Valor de 1 {currency} en COP en la fecha de emisión.
                  Obligatorio por DIAN (AT v1.9 FAR) cuando la factura no es en COP.
                </p>
              </div>
            )}
          </div>

          {/* ─── Anticipo pre-factura (AT v1.9 FBD) ─────────────── */}
          <div className="mt-4 rounded-md border border-border bg-bg-app p-4">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={hasPrepayment}
                onChange={(e) => setHasPrepayment(e.target.checked)}
              />
              ¿La factura incluye un anticipo recibido antes de la emisión?
            </label>
            <p className="mt-1 text-xs text-fg-muted">
              Activar si el cliente pagó parcialmente antes de la factura.
              DIAN exige <code>cac:PrepaidPayment</code> con ID + monto + fecha
              (AT v1.9 FBD). El monto del anticipo no puede exceder el total.
            </p>
            {hasPrepayment && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium" htmlFor="prepaid_id">
                    Identificador del pago *
                  </label>
                  <input
                    id="prepaid_id"
                    type="text"
                    required
                    value={prepaidId}
                    onChange={(e) => setPrepaidId(e.target.value)}
                    placeholder="Ej: TRANSFER-2026-001"
                    className={inputCls + ' mt-1'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium" htmlFor="prepaid_amount">
                    Monto del anticipo *
                  </label>
                  <input
                    id="prepaid_amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={prepaidAmount}
                    onChange={(e) => setPrepaidAmount(e.target.value)}
                    placeholder="0.00"
                    className={inputCls + ' mt-1'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium" htmlFor="prepaid_received_date">
                    Fecha recibido *
                  </label>
                  <input
                    id="prepaid_received_date"
                    type="date"
                    required
                    value={prepaidReceivedDate}
                    onChange={(e) => setPrepaidReceivedDate(e.target.value)}
                    className={inputCls + ' mt-1'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium" htmlFor="prepaid_paid_date">
                    Fecha del pago (opcional)
                  </label>
                  <input
                    id="prepaid_paid_date"
                    type="date"
                    value={prepaidPaidDate}
                    onChange={(e) => setPrepaidPaidDate(e.target.value)}
                    className={inputCls + ' mt-1'}
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ─── Líneas ───────────────────────────────────────── */}
        <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
              Líneas
            </h2>
            <button
              type="button"
              onClick={addLine}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-app px-3 py-1.5 text-xs font-medium hover:bg-bg-elevated"
            >
              <Plus className="h-3.5 w-3.5" /> Agregar línea
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {lines.map((line, idx) => {
              const calc = computeLine(line)
              return (
                <div key={idx} className="rounded-md border border-border bg-bg-app p-4">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-semibold text-fg-muted">
                      Línea {idx + 1}
                    </span>
                    {lines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        className="text-xs text-error-fg hover:underline"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-medium">Producto (catálogo)</label>
                      <select
                        value={line.product_id ?? ''}
                        onChange={(e) => {
                          const p = products.data?.items.find((x) => x.id === e.target.value)
                          setProductAtLine(idx, p ?? null)
                        }}
                        className={inputCls + ' mt-1'}
                      >
                        <option value="">— Línea inline —</option>
                        {products.data?.items.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium">Descripción *</label>
                      <input
                        type="text"
                        value={line.description}
                        onChange={(e) => updateLine(idx, { description: e.target.value })}
                        className={inputCls + ' mt-1'}
                      />
                    </div>
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-4">
                    <FieldNum
                      label="Cantidad *"
                      value={line.quantity}
                      onChange={(v) => updateLine(idx, { quantity: v })}
                    />
                    <FieldNum
                      label="Precio unitario *"
                      value={line.unit_price}
                      onChange={(v) => updateLine(idx, { unit_price: v })}
                    />
                    <FieldNum
                      label="Descuento"
                      value={line.line_discount}
                      onChange={(v) => updateLine(idx, { line_discount: v })}
                    />
                    <FieldNum
                      label="IVA %"
                      value={line.tax_iva_percent}
                      onChange={(v) => updateLine(idx, { tax_iva_percent: v })}
                      disabled={line.tax_iva_excluded}
                    />
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs text-fg-secondary">
                      <input
                        type="checkbox"
                        checked={line.tax_iva_excluded}
                        onChange={(e) =>
                          updateLine(idx, { tax_iva_excluded: e.target.checked })
                        }
                      />
                      IVA excluido
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-fg-secondary">
                      INC %
                      <input
                        type="text"
                        inputMode="decimal"
                        value={line.tax_inc_percent}
                        onChange={(e) =>
                          updateLine(idx, {
                            tax_inc_percent: e.target.value.replace(/[^\d.]/g, ''),
                          })
                        }
                        className="w-16 rounded-md border border-border bg-bg-surface px-2 py-1 text-right font-mono text-xs"
                      />
                    </label>
                  </div>

                  {/* Cálculo en vivo */}
                  <div className="mt-3 grid gap-1 rounded-md bg-bg-surface p-3 text-xs">
                    <Pair label="Subtotal" value={money.format(calc.subtotal)} />
                    <Pair label="IVA" value={money.format(calc.iva)} />
                    {calc.inc > 0 && <Pair label="INC" value={money.format(calc.inc)} />}
                    <Pair
                      label="Total línea"
                      value={money.format(calc.total)}
                      bold
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* ─── Totales agregados ──────────────────────────── */}
        <section className="mt-6 rounded-xl border-2 border-brand-500/40 bg-brand-50/30 p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-brand-700">
            Totales factura
          </h2>
          <dl className="mt-3 grid gap-2 text-sm">
            <Pair label="Subtotal" value={money.format(totals.subtotal)} />
            <Pair label="IVA total" value={money.format(totals.iva)} />
            {totals.inc > 0 && <Pair label="INC total" value={money.format(totals.inc)} />}
            <div className="my-1 border-t border-brand-500/30" />
            <Pair label="GRAN TOTAL" value={money.format(totals.total)} bold large />
          </dl>
        </section>

        {/* ─── Notas ─── */}
        <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <label className="block text-sm font-medium" htmlFor="notes">
            Observaciones (opcional)
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className={inputCls + ' mt-1'}
          />
        </section>

        <div className="mt-6 flex items-center justify-end">
          <button
            type="button"
            disabled={!canSubmit || create.isPending}
            onClick={() => create.mutate()}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {create.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Guardando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" /> Guardar borrador
              </>
            )}
          </button>
        </div>
      </div>
    </AppShell>
  )
}

function FieldNum({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  return (
    <div>
      <label className="block text-xs font-medium">{label}</label>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
        disabled={disabled}
        className={inputCls + ' mt-1 text-right font-mono'}
      />
    </div>
  )
}

function Pair({
  label,
  value,
  bold,
  large,
}: {
  label: string
  value: string
  bold?: boolean
  large?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className={`${bold ? 'font-semibold' : 'text-fg-muted'}`}>{label}</dt>
      <dd
        className={`font-mono ${bold ? 'font-bold' : ''} ${
          large ? 'text-lg text-brand-700' : 'text-sm'
        }`}
      >
        {value}
      </dd>
    </div>
  )
}

const inputCls =
  'block w-full rounded-md border border-border bg-bg-app px-3 py-2 text-sm text-fg-primary placeholder-fg-muted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-60'
